/**
 * AtlasLoader — load a manifest of sprite frames into PixiJS Textures.
 *
 * # What this file owns
 *
 *   1. **Manifest loading** — accept an `AtlasManifest` and return a map
 *      of frame ID → Texture. Individual entries load directly via
 *      PixiJS `Assets.load`; strip entries load the strip texture once
 *      and produce per-frame sub-textures by slicing.
 *   2. **Per-URL texture cache** — calling `load()` with overlapping
 *      manifests reuses textures. Calling `load()` twice with the same
 *      manifest is essentially free after the first call.
 *   3. **Missing-asset fallback** — any individual load that throws
 *      (404, network error, decode failure) falls back to a magenta
 *      placeholder texture and logs a `console.warn`. Game keeps running.
 *   4. **NEAREST scaleMode enforcement (Layer 4b)** — every loaded
 *      Texture (including the magenta placeholder) has its underlying
 *      TextureSource forced to `scaleMode: 'nearest'`. This preserves
 *      crisp pixel-art edges and keeps source palette colors intact for
 *      the palette-swap shader. See `applyTextureScaleMode` for full
 *      rationale.
 *   5. **Dispose** — releases every cached texture's GPU resources via
 *      `texture.destroy(true)`. Idempotent.
 *
 * # What this file does NOT own
 *
 *   - Sprite rendering / sprite anchor / sprite scale (Layer 4 — `spriteRenderer.ts`)
 *   - Tile rendering (Layer 3 — `tileRenderer.ts`)
 *   - Animation frame timing — Layer 4 picks `currentFrame` from a
 *     LoadedAtlas via the engine ID convention; this file just provides
 *     the textures.
 *   - Manifest construction — `atlasManifests.ts` is the data source.
 *
 * # Magenta placeholder convention (LOCKED Layer 2)
 *
 * When a frame fails to load, we substitute a 64×64 magenta texture
 * with a black `?` glyph rendered via Canvas2D and a `console.warn` of
 * the failed URL. The placeholder is shared across all failed loads
 * (one Texture instance, reused as N substitutions) to avoid
 * allocating per-failure.
 *
 * Why magenta: industry-standard "missing texture" color (Source engine,
 * Half-Life 2, Unity, Unreal, Godot). Visually unmissable in the running
 * game, immediately tells the dev "this asset wasn't loaded." Production
 * smoke tests can grep `console.warn` output to assert no missing assets
 * shipped to users.
 *
 * # Concurrent load semantics
 *
 * `load(manifest)` runs all individual frame loads concurrently via
 * `Promise.all`. Strip entries load the strip first, then synchronously
 * slice into per-frame Textures (no further async work). Total wall
 * time is bounded by the slowest individual asset, not the sum.
 *
 * Browsers cap concurrent connections per origin (HTTP/1: ~6, HTTP/2:
 * effectively 100+). For the 768-frame Prime manifest, the browser
 * pipelines requests; total load time on fiber is ~1-2 seconds.
 *
 * # Cache scope
 *
 * The cache is per-AtlasLoader instance, keyed by URL. Two loaders =
 * two caches; this is intentional — a CombatScreen loader and a future
 * UIPortrait loader shouldn't share state. If a multi-screen
 * deduplication is ever needed, it can be added as a singleton wrapper
 * over a single AtlasLoader.
 *
 * # Test contract
 *
 * The companion test mocks `pixi.js` `Assets.load` to resolve with stub
 * Texture objects (or reject with a fake error to drive the magenta
 * fallback path). The `createMagentaPlaceholder` helper is also
 * separately injectable so tests don't need a real Canvas2D context.
 *
 * Test stubs MUST expose a settable `source.scaleMode` property to
 * support the Layer 4b NEAREST application — see `applyTextureScaleMode`.
 *
 * @see {@link ./atlasManifests} — manifest definitions
 * @see {@link ./pixiMount} — provides the running Application context
 *   that texture loads target
 */

import { Assets, Texture, Rectangle } from 'pixi.js';
import {
  type AtlasManifest,
  type AtlasFrame,
  type IndividualFrameEntry,
  type StripFrameEntry,
} from './atlasManifests';

// ─────────────────────────────────────────────────────────────────────────────
// Magenta placeholder factory
//
// We allow injection so tests can supply a stub Texture without needing a
// Canvas2D context. The default factory uses real Canvas2D.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Function type that produces a "missing texture" placeholder.
 *
 * Default implementation paints a 64×64 magenta canvas with a black `?`
 * glyph and constructs a PixiJS Texture from it. Tests inject a stub
 * factory to avoid Canvas2D in Node-environment tests.
 */
export type MagentaPlaceholderFactory = () => Texture;

/**
 * Default magenta-placeholder factory. Uses Canvas2D; only callable in
 * environments where `document.createElement('canvas')` is available.
 */
export function createDefaultMagentaPlaceholder(): Texture {
  // Defensive: in Node without JSDOM, document is undefined. We surface
  // a clear error rather than a confusing TypeError on .createElement.
  if (typeof document === 'undefined') {
    throw new Error(
      'createDefaultMagentaPlaceholder: requires browser DOM (document.createElement). ' +
        'In tests, inject a stub factory via AtlasLoaderOptions.magentaFactory.',
    );
  }

  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error(
      'createDefaultMagentaPlaceholder: 2D canvas context unavailable',
    );
  }

  // Magenta fill — industry-standard missing-texture color.
  ctx.fillStyle = '#ff00ff';
  ctx.fillRect(0, 0, 64, 64);

  // Black "?" glyph centered for visibility.
  ctx.fillStyle = '#000000';
  ctx.font = 'bold 48px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('?', 32, 36);

  return Texture.from(canvas);
}

// ─────────────────────────────────────────────────────────────────────────────
// LoadedAtlas — the result type
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The result of loading a manifest. Maps frame ID → Texture.
 *
 *   - Individual frame entries produce one Texture per entry, keyed by
 *     the entry's `id`.
 *   - Strip frame entries produce N Textures per entry, keyed by
 *     `${entry.id}.${frameIndex}` where frameIndex is 0-based.
 *
 * Failed loads produce a magenta placeholder texture under the same key.
 * The caller can detect failures by checking against the loader's
 * `getMissingFrameIds()` method, or by visual inspection in the running
 * game (magenta sprites are unmissable).
 */
export interface LoadedAtlas {
  /** Look up a texture by frame ID. Returns undefined if the ID was not in the manifest. */
  get(frameId: string): Texture | undefined;

  /** Test whether a frame ID is present (loaded or magenta-substituted). */
  has(frameId: string): boolean;

  /** All frame IDs that this atlas covers, in insertion order. */
  ids(): string[];
}

/**
 * Internal mutable LoadedAtlas implementation. Construction-time only;
 * exposed to consumers via the read-only `LoadedAtlas` interface.
 */
class LoadedAtlasImpl implements LoadedAtlas {
  private readonly textures = new Map<string, Texture>();

  set(frameId: string, texture: Texture): void {
    this.textures.set(frameId, texture);
  }

  get(frameId: string): Texture | undefined {
    return this.textures.get(frameId);
  }

  has(frameId: string): boolean {
    return this.textures.has(frameId);
  }

  ids(): string[] {
    return Array.from(this.textures.keys());
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// AtlasLoader options + lifecycle
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Optional callback invoked as frames complete loading. Useful for
 * driving a progress bar UI in CombatScreen during initial atlas load.
 *
 * Called once per individual frame entry AND once per strip entry
 * (strip slicing is treated as a single completion, since it's
 * synchronous after the strip texture loads). Total invocations =
 * `manifest.frames.length`.
 */
export type LoadProgressCallback = (loaded: number, total: number) => void;

/**
 * Configuration for `AtlasLoader`.
 */
export interface AtlasLoaderOptions {
  /**
   * Optional override for the magenta placeholder factory. Production
   * code uses the default Canvas2D-based factory. Tests inject a stub
   * that produces a fake texture without needing DOM.
   */
  readonly magentaFactory?: MagentaPlaceholderFactory;
}

/**
 * Owner of texture loading + cache + dispose lifecycle. Construct once
 * per CombatScreen (or other rendering surface); call `load()` with one
 * or more manifests; call `dispose()` on teardown.
 */
export class AtlasLoader {
  private readonly cache = new Map<string, Texture>();
  private readonly magentaFactory: MagentaPlaceholderFactory;
  private magentaPlaceholder: Texture | null = null;
  private missingFrameIds: string[] = [];
  private disposed = false;

  constructor(options: AtlasLoaderOptions = {}) {
    this.magentaFactory =
      options.magentaFactory ?? createDefaultMagentaPlaceholder;
  }

  /**
   * Load every frame in the manifest. Returns a `LoadedAtlas` with
   * one texture per frame ID. Failed loads are substituted with the
   * magenta placeholder; check `getMissingFrameIds()` to enumerate
   * which loads failed.
   *
   * Cached: if a frame's URL was already loaded by a prior call, the
   * cached Texture is reused. Calling `load(manifest)` twice with the
   * same manifest does not re-fetch.
   *
   * @throws Error if the loader has been disposed
   */
  async load(
    manifest: AtlasManifest,
    onProgress?: LoadProgressCallback,
  ): Promise<LoadedAtlas> {
    if (this.disposed) {
      throw new Error('AtlasLoader.load: instance has been disposed');
    }

    const result = new LoadedAtlasImpl();
    const total = manifest.frames.length;
    let loaded = 0;

    // Run all frame loads concurrently. Each load is independent; one
    // failure does not block siblings (the inner try/catch handles it).
    await Promise.all(
      manifest.frames.map(async (frame) => {
        await this.loadOneFrame(frame, manifest.name, result);
        loaded += 1;
        if (onProgress) {
          // Defensive: progress callback is user-supplied; a thrown
          // error should not abort other loads.
          try {
            onProgress(loaded, total);
          } catch (err) {
            console.warn(
              `[AtlasLoader] progress callback threw for "${manifest.name}":`,
              err,
            );
          }
        }
      }),
    );

    return result;
  }

  /**
   * Returns the list of frame IDs that fell back to the magenta
   * placeholder during any prior `load()` call. Useful for production
   * smoke-test assertions: "after loading the Compact biome manifest,
   * `getMissingFrameIds().length` must equal 0".
   */
  getMissingFrameIds(): readonly string[] {
    return [...this.missingFrameIds];
  }

  /**
   * Tear down all cached textures and the magenta placeholder. Idempotent.
   * After dispose, `load()` throws.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Pass `true` to texture.destroy() to also destroy the underlying
    // TextureSource (the GPU buffer). Without this, GPU memory leaks
    // until garbage collection runs (which may be never on long-lived
    // contexts).
    for (const tex of this.cache.values()) {
      try {
        tex.destroy(true);
      } catch (err) {
        // A texture that fails to destroy is unlikely to cause issues
        // beyond its own leak; log and continue so we don't leak siblings.
        console.warn('[AtlasLoader] texture.destroy threw:', err);
      }
    }
    this.cache.clear();

    if (this.magentaPlaceholder !== null) {
      try {
        this.magentaPlaceholder.destroy(true);
      } catch (err) {
        console.warn('[AtlasLoader] magenta placeholder destroy threw:', err);
      }
      this.magentaPlaceholder = null;
    }

    this.missingFrameIds = [];
  }

  // ───────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Lazily construct and return the magenta placeholder. Single instance
   * per loader; reused across all failed loads.
   *
   * The placeholder gets NEAREST scaleMode applied at construction time
   * so it renders crisp at any sprite scale rather than blurry-bilinear.
   */
  private getMagentaPlaceholder(): Texture {
    if (this.magentaPlaceholder === null) {
      this.magentaPlaceholder = this.magentaFactory();
      this.applyTextureScaleMode(this.magentaPlaceholder);
    }
    return this.magentaPlaceholder;
  }

  /**
   * Load a single AtlasFrame entry into the result. Dispatches on the
   * `kind` discriminator.
   */
  private async loadOneFrame(
    frame: AtlasFrame,
    manifestName: string,
    result: LoadedAtlasImpl,
  ): Promise<void> {
    if (frame.kind === 'individual') {
      await this.loadIndividual(frame, manifestName, result);
    } else {
      await this.loadStrip(frame, manifestName, result);
    }
  }

  /**
   * Load one individual-frame entry. On success, store the Texture
   * keyed by frame.id. On failure, substitute the magenta placeholder
   * and record the failure in missingFrameIds.
   */
  private async loadIndividual(
    frame: IndividualFrameEntry,
    manifestName: string,
    result: LoadedAtlasImpl,
  ): Promise<void> {
    // Cache hit short-circuits both the network and the error path.
    const cached = this.cache.get(frame.path);
    if (cached) {
      result.set(frame.id, cached);
      return;
    }

    try {
      // PixiJS v8 Assets.load returns the Texture directly (or array if
      // passed array). We pass a single string so we get a single Texture.
      const texture = (await Assets.load(frame.path)) as Texture;
      this.applyTextureScaleMode(texture);
      this.cache.set(frame.path, texture);
      result.set(frame.id, texture);
    } catch (err) {
      console.warn(
        `[AtlasLoader] failed to load "${frame.id}" from "${frame.path}" (manifest "${manifestName}"):`,
        err,
      );
      this.missingFrameIds.push(frame.id);
      result.set(frame.id, this.getMagentaPlaceholder());
    }
  }

  /**
   * Load one strip-frame entry. Loads the strip texture once, then
   * synchronously slices it into N per-frame textures via PixiJS's
   * frame-rectangle constructor.
   *
   * On strip-load failure: substitute magenta for ALL N frame keys
   * (consistent with individual-frame failure semantics).
   *
   * The strip's TextureSource gets NEAREST scaleMode applied once after
   * load. Per-frame slice Textures share that TextureSource, so all
   * sliced frames inherit NEAREST automatically — no per-slice
   * application needed.
   */
  private async loadStrip(
    frame: StripFrameEntry,
    manifestName: string,
    result: LoadedAtlasImpl,
  ): Promise<void> {
    let stripTexture: Texture | null = this.cache.get(frame.path) ?? null;

    if (stripTexture === null) {
      try {
        stripTexture = (await Assets.load(frame.path)) as Texture;
        this.applyTextureScaleMode(stripTexture);
        this.cache.set(frame.path, stripTexture);
      } catch (err) {
        console.warn(
          `[AtlasLoader] failed to load strip "${frame.id}" from "${frame.path}" (manifest "${manifestName}"):`,
          err,
        );
        // Substitute magenta for every frame key the strip would have
        // produced. Caller can detect via missingFrameIds enumeration.
        const placeholder = this.getMagentaPlaceholder();
        for (let i = 0; i < frame.frameCount; i++) {
          const key = `${frame.id}.${i}`;
          this.missingFrameIds.push(key);
          result.set(key, placeholder);
        }
        return;
      }
    }

    // Slice the strip into per-frame textures. Each frame is a Texture
    // sharing the strip's underlying TextureSource but with its own
    // frame Rectangle. PixiJS v8 supports this via the Texture
    // constructor with `{ source, frame }`.
    //
    // Per-slice scaleMode application is unnecessary: slices share
    // stripTexture.source, and applyTextureScaleMode was already called
    // on the strip above. The slices inherit NEAREST automatically.
    for (let i = 0; i < frame.frameCount; i++) {
      const frameRect = new Rectangle(
        i * frame.frameWidth,
        0,
        frame.frameWidth,
        frame.frameHeight,
      );
      const sliceTexture = new Texture({
        source: stripTexture.source,
        frame: frameRect,
      });
      const key = `${frame.id}.${i}`;
      result.set(key, sliceTexture);
    }
  }

  /**
   * Force NEAREST sampling on a loaded Texture's underlying source.
   *
   * # Why NEAREST
   *
   * The Aevon's Wake sprite assets (AUTeddy "Cyber Marksman" pack and
   * future biome assets) are pixel-art with hard edges and indexed
   * palettes. PixiJS v8 defaults TextureSource scaleMode to `'linear'`
   * (bilinear interpolation), which produces:
   *
   *   1. Soft, blurry edges when sprites are scaled to non-1:1 sizes
   *      (which is most of the time given camera zoom + DPR).
   *   2. Intermediate color values at sprite boundaries that don't
   *      exist in the source palette — these break the palette-swap
   *      shader's HSL conversion (Layer 4b), producing color-banding
   *      artifacts.
   *
   * NEAREST sampling is what every solo-dev pixel-art game ships
   * (Brotato / Vampire Survivors / Halls of Torment / Hades all use
   * NEAREST for sprites). It preserves crisp pixel edges and keeps
   * source palette colors intact.
   *
   * # Why on TextureSource not Texture
   *
   * In PixiJS v8, `scaleMode` lives on `TextureSource` (the GPU asset),
   * not on `Texture` (which is a frame view into a source). For atlas
   * strips, multiple Textures share one TextureSource — setting
   * `texture.source.scaleMode = 'nearest'` once propagates to every
   * sliced frame sharing that source. Setting it on the Texture would
   * be a no-op (Texture has no scaleMode field).
   *
   * # Why mutation after Assets.load()
   *
   * PixiJS v8's TextureSource.scaleMode setter triggers a re-upload on
   * next render. Setting it post-load is supported and idiomatic; the
   * `Assets.load` API doesn't accept a scaleMode option directly in v8.
   *
   * # Idempotency
   *
   * Calling this multiple times on the same TextureSource is a no-op
   * after the first call (setting scaleMode to its current value is
   * cheap and correct). Strip frames sharing a TextureSource will hit
   * this multiple times during slicing; that's fine.
   *
   * # Test contract
   *
   * Test stubs that mock `Assets.load` MUST return a stub Texture whose
   * `source` field is an object with a settable `scaleMode` property.
   * The minimal stub shape is:
   *
   *     { source: { scaleMode: 'linear' } }
   *
   * Tests can read back `texture.source.scaleMode` after load to verify
   * NEAREST was applied.
   */
  private applyTextureScaleMode(texture: Texture): void {
    texture.source.scaleMode = 'nearest';
  }
}
